import axios from 'axios';

const BASE_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';

const getAuthHeaders = (json = false) => {
  const token = localStorage.getItem('token');
  return {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

export interface ExperimentDto {
  id: string;
  batchNumber: string;
  createdAt: string;
}

export interface ExperimentResultItemDto {
  id: string;
  experimentId: string;
  analysis?: string | null;
  analysisVariation?: string | null;
  sampleResult?: string | null;
  testValue: string;
  unit: string;
}

export interface CompareRowDto {
  id?: number;
  batchNumber: string;
  parameter: string;
  testMethod: string;
  uom: string;
  analysis: string;
  units: string;
  specMin: string;
  specMax: string;
  testValue: string;
  testResult: string;
}

export interface ReleaseTaskDto {
  id?: string | number;
  batchNumber?: string;
  result?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface COATemplateItemDto {
  id?: number | string;
  batchNumber?: string;
  parameter?: string;
  classification?: string;
  testMethod?: string;
  uom?: string;
  specMin?: string | number | null;
  specMax?: string | number | null;
}

export interface COATemplateDto {
  id?: string | number;
  batchNumber?: string;
  productDescription?: string;
  specNumber?: string;
  sampleSubmissionRefNumber?: string;
  manufactureDate?: string;
  expiryDate?: string;
  description?: string | null;
  preparedBy?: string | null;
  preparedByTitle?: string | null;
  preparedBySignature?: string | null;
  preparedByDate?: string | null;
  releaseBy?: string | null;
  releaseByTitle?: string | null;
  releaseBySignature?: string | null;
  releaseByDate?: string | null;
  createdAt?: string;
  updatedAt?: string;
  items?: COATemplateItemDto[];
}

export interface BackendMatchOptionDto {
  id?: string;
  analysis?: string;
  unit?: string;
  testValueRaw?: string;
  score?: number;
}

export interface BackendMatchRowDto {
  rowKey?: string;
  parameter?: string;
  selectedMatchId?: string | null;
  defaultSelectedId?: string | null;
  optionIds?: string[];
  options?: BackendMatchOptionDto[];
}

export interface ReleaseParameterMappingDto {
  rowKey: string;
  parameter: string;
  analysis: string;
  selectedMatchId: string;
  testMethod?: string;
  uom?: string;
  unit?: string;
}

export interface SaveReleaseParameterMappingsPayload {
  batchNumber: string;
  mappings: ReleaseParameterMappingDto[];
}

export const getExperiments = async (params: Record<string, string>): Promise<ExperimentDto[]> => {
  try {
    const response = await axios.get<ExperimentDto[]>(`${BASE_API_URL}/experiments`, {
      params,
      headers: getAuthHeaders(),
    });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error: any) {
    throw new Error(error.response?.data?.message ?? error.message ?? 'Load experiments failed');
  }
};

export const getReleaseTasks = async (params: Record<string, string>): Promise<any[]> => {
  try {
    const response = await axios.get<any[]>(`${BASE_API_URL}/ReleaseTasks`, {
      params,
      headers: getAuthHeaders(),
    });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error: any) {
    throw new Error(error.response?.data?.message ?? error.message ?? 'Load release tasks failed');
  }
};

export const getSpecifications = async (params: Record<string, string>): Promise<any[]> => {
  try {
    const response = await axios.get<any[]>(`${BASE_API_URL}/specifications`, {
      params,
      headers: getAuthHeaders(),
    });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error: any) {
    throw new Error(error.response?.data?.message ?? error.message ?? 'Load specifications failed');
  }
};

export const getCOATemplates = async (params: Record<string, string>): Promise<COATemplateDto[]> => {
  try {
    const response = await axios.get<COATemplateDto[]>(`${BASE_API_URL}/COATemplates`, {
      params,
      headers: getAuthHeaders(),
    });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error: any) {
    throw new Error(error.response?.data?.message ?? error.message ?? 'Load COA templates failed');
  }
};

export const getCOATemplateById = async (id: string | number): Promise<COATemplateDto> => {
  try {
    const response = await axios.get<COATemplateDto>(`${BASE_API_URL}/COATemplates/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message ?? error.message ?? 'Load COA template failed');
  }
};

export const getExperimentResultItems = async (params: Record<string, string>): Promise<ExperimentResultItemDto[]> => {
  try {
    const response = await axios.get<ExperimentResultItemDto[]>(`${BASE_API_URL}/experimentresultitems`, {
      params,
      headers: getAuthHeaders(),
    });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error: any) {
    throw new Error(error.response?.data?.message ?? error.message ?? 'Load experiment result items failed');
  }
};

export const getReleaseCompareRowsByBatch = async (batchNumber: string): Promise<CompareRowDto[]> => {
  try {
    const response = await axios.get<CompareRowDto[]>(`${BASE_API_URL}/ReleaseTasksCompare/batch/${encodeURIComponent(batchNumber)}`, {
      headers: getAuthHeaders(),
    });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error: any) {
    throw new Error(error.response?.data?.message ?? error.message ?? 'Load comparison result failed');
  }
};

export const deleteReleaseCompareRowsByBatch = async (ids: number[]): Promise<void> => {
  if (ids.length === 0) return;

  try {
    await axios.post(`${BASE_API_URL}/ReleaseTasksCompare/batchDelete`, ids, {
      headers: getAuthHeaders(true),
    });
  } catch (error: any) {
    throw new Error(error.response?.data?.message ?? error.message ?? 'Delete existing compare rows failed');
  }
};

export const createReleaseCompareRowsByBatch = async (items: Array<Record<string, any>>): Promise<void> => {
  try {
    await axios.post(`${BASE_API_URL}/ReleaseTasksCompare/batch`, items, {
      headers: getAuthHeaders(true),
    });
  } catch (error: any) {
    throw new Error(error.response?.data?.message ?? error.message ?? 'Save compare rows failed');
  }
};

export const saveReleaseParameterMappings = async (payload: SaveReleaseParameterMappingsPayload): Promise<void> => {
  try {
    await axios.post(`${BASE_API_URL}/mappings/batch`, payload, {
      headers: getAuthHeaders(true),
    });
  } catch (error: any) {
    throw new Error(error.response?.data?.message ?? error.message ?? 'Save parameter mapping failed');
  }
};

export const getReleaseTaskById = async (id: string): Promise<any> => {
  try {
    const response = await axios.get(`${BASE_API_URL}/ReleaseTasks/${encodeURIComponent(id)}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message ?? error.message ?? 'Load release task failed');
  }
};

export const createReleaseTask = async (payload: Record<string, any>): Promise<any> => {
  try {
    const response = await axios.post(`${BASE_API_URL}/ReleaseTasks`, payload, {
      headers: getAuthHeaders(true),
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message ?? error.message ?? 'Save release task failed');
  }
};

export const updateReleaseTask = async (id: string, payload: Record<string, any>): Promise<any> => {
  try {
    const response = await axios.put(`${BASE_API_URL}/ReleaseTasks/${encodeURIComponent(id)}`, payload, {
      headers: getAuthHeaders(true),
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message ?? error.message ?? 'Update release task failed');
  }
};

export const getReleaseMatchRows = async (payload: Record<string, any>): Promise<BackendMatchRowDto[] | null> => {
  const endpoints = [
    `${BASE_API_URL}/ReleaseTasksCompare/match`,
  ];

  for (const endpoint of endpoints) {
    try {
      const response = await axios.post(endpoint, payload, {
        headers: getAuthHeaders(true),
      });

      const data = response.data;
      const rows = Array.isArray(data) ? data : (data?.rows || data?.Rows || []);
      if (Array.isArray(rows) && rows.length > 0) {
        return rows as BackendMatchRowDto[];
      }
    } catch {
      // try next endpoint
    }
  }

  return null;
};
